import React, {
  Fragment,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Loading, Title, Translate, useTranslate, useVersion } from "react-admin";
import { GoogleMap } from "../GoogleMap";
import {
  ControlPosition,
  LiveTrackEvent,
  MapImperativeControls,
} from "../types";
import {
  Button,
  Paper,
  Theme,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { Warning } from "@material-ui/icons";
import {
  LiveTrackVehicleDto,
  Odometer,
} from "../../../common/data/service-proxies";
import { AnonymousLocationLiveTrack } from "./AnonymousLocationLiveTrack";
import { AuthenticatedLocationLiveTrack } from "./AuthenticatedLocationLiveTrack";
import { GoogleMapLiveTrackingMarker } from "./GoogleMapLiveTrackingMarker";
import { LocationLiveTrackTitle } from "./LocationLiveTrackTitle";
import { useLiveTrackingMotion } from "./useLiveTrackingMotion";

interface Props {
  id?: string;
  tenantId?: string;
}

interface InfoWindowProps {
  driverFullName?: string | null;
  drivingSpeed?: string;
  drivingSpeedUnit?: string | null;
  location?: string | null;
  onRecenter?: () => void;
  showRecenter?: boolean;
  statusMessage?: string;
  theme: Theme;
  trailers?: string | null;
  translate: Translate;
}

interface TrailPoint {
  lat: number;
  lng: number;
}

const LIVE_TRACKING_DEFAULT_ZOOM = 16;

const speedFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const isNumber = (input: unknown): input is number => !isNaN(Number(input));

const hasValidCoordinates = (
  event?: LiveTrackEvent | null
): event is LiveTrackEvent & { lat: number; lng: number } =>
  typeof event?.lat === "number" &&
  typeof event?.lng === "number" &&
  Number.isFinite(event.lat) &&
  Number.isFinite(event.lng);

const formatSpeedValue = (value?: number | null) =>
  typeof value === "number" && Number.isFinite(value)
    ? speedFormatter.format(value)
    : "-";

const buildLiveTrailExtension = (
  trailPath: TrailPoint[],
  trackingLocation?: LiveTrackEvent
) => {
  if (trailPath.length < 2 || !hasValidCoordinates(trackingLocation)) {
    return undefined;
  }

  const lastTrailPoint = trailPath[trailPath.length - 1];
  if (
    Math.abs(lastTrailPoint.lat - trackingLocation.lat) <= 0.0000001 &&
    Math.abs(lastTrailPoint.lng - trackingLocation.lng) <= 0.0000001
  ) {
    return undefined;
  }

  return [
    lastTrailPoint,
    { lat: trackingLocation.lat, lng: trackingLocation.lng },
  ];
};

const getPlanarOffsetMeters = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
) => {
  const averageLatitudeRadians = ((fromLat + toLat) / 2) * (Math.PI / 180);

  return {
    east:
      ((toLng - fromLng) * Math.PI * 6_371_000 * Math.cos(averageLatitudeRadians)) /
      180,
    north: ((toLat - fromLat) * Math.PI * 6_371_000) / 180,
  };
};

const shouldClipLatestTrailPoint = (
  trailPath: TrailPoint[],
  trackingLocation?: LiveTrackEvent
) => {
  if (trailPath.length < 2 || !hasValidCoordinates(trackingLocation)) {
    return false;
  }

  const previousTrailPoint = trailPath[trailPath.length - 2];
  const lastTrailPoint = trailPath[trailPath.length - 1];
  const segmentOffset = getPlanarOffsetMeters(
    previousTrailPoint.lat,
    previousTrailPoint.lng,
    lastTrailPoint.lat,
    lastTrailPoint.lng
  );
  const segmentLengthMeters = Math.hypot(
    segmentOffset.east,
    segmentOffset.north
  );
  if (segmentLengthMeters < 1) {
    return false;
  }

  const markerOffset = getPlanarOffsetMeters(
    previousTrailPoint.lat,
    previousTrailPoint.lng,
    trackingLocation.lat,
    trackingLocation.lng
  );
  const markerProgressMeters =
    (markerOffset.east * segmentOffset.east +
      markerOffset.north * segmentOffset.north) /
    segmentLengthMeters;

  return markerProgressMeters < segmentLengthMeters - 1;
};

const getRenderedTrailRoutes = (
  trailPath: TrailPoint[],
  trackingLocation?: LiveTrackEvent
) => {
  if (!trailPath.length) {
    return {
      highlightedRoute: undefined,
      visitedRoute: undefined,
    };
  }

  if (shouldClipLatestTrailPoint(trailPath, trackingLocation)) {
    const visitedRoute = trailPath.slice(0, -1);
    const highlightedRoute =
      visitedRoute.length && hasValidCoordinates(trackingLocation)
        ? [
          visitedRoute[visitedRoute.length - 1],
          { lat: trackingLocation.lat, lng: trackingLocation.lng },
        ]
        : undefined;

    return {
      highlightedRoute,
      visitedRoute: visitedRoute.length ? visitedRoute : undefined,
    };
  }

  return {
    highlightedRoute: buildLiveTrailExtension(trailPath, trackingLocation),
    visitedRoute: trailPath.length ? trailPath : undefined,
  };
};

const toSeedTrackEvent = (
  vehicle?: LiveTrackVehicleDto
): LiveTrackEvent | undefined => {
  const currentLocation = vehicle?.currentLocation;
  if (!currentLocation) {
    return undefined;
  }

  return {
    lat: currentLocation.lat,
    lng: currentLocation.lng,
    bearing: currentLocation.bearing,
    speed: currentLocation.drivingSpeed,
    driverName: currentLocation.driverFullName,
    trailers: currentLocation.attachedTrailer,
    geolocation: currentLocation.location,
  };
};

const InfoWindow = ({
  location,
  driverFullName,
  drivingSpeed,
  drivingSpeedUnit,
  onRecenter,
  showRecenter,
  trailers,
  statusMessage,
  theme,
  translate,
}: InfoWindowProps) => (
  <div className="info-window-content">
    <ThemeProvider theme={theme}>
      <Paper
        className="info-window-content-paper"
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <div className="info-window-content-group">
          <div>
            <Typography variant="body2">
              {translate("resources.locations-current.labels.driverName")}
            </Typography>
          </div>
          <div>
            <Typography variant="body2">{driverFullName}</Typography>
          </div>
        </div>
        <div className="info-window-content-group">
          <div>
            <Typography variant="body2">Location</Typography>
          </div>
          <div>
            <Typography variant="body2">{location}</Typography>
          </div>
        </div>
        <div className="info-window-content-group">
          <div>
            <Typography variant="body2">
              {translate("resources.locations-current.labels.trailers")}
            </Typography>
          </div>
          <div>
            <Typography variant="body2">
              {trailers ?? translate("resources.locations-current.labels.none")}
            </Typography>
          </div>
        </div>
        <div className="info-window-content-group">
          <div>
            <Typography variant="body2">
              {translate("resources.locations-current.labels.drivingSpeed")}
            </Typography>
          </div>
          <div>
            <Typography variant="body2">
              {drivingSpeed} {drivingSpeedUnit}
            </Typography>
          </div>
        </div>
        {!!statusMessage && (
          <div
            style={{
              alignItems: "center",
              backgroundColor: theme.palette.warning.main,
              borderRadius: 4,
              color: theme.palette.warning.contrastText,
              display: "flex",
              gap: 8,
              marginTop: 8,
              padding: 8,
            }}
          >
            <Warning />
            <Typography variant="body2">{statusMessage}</Typography>
          </div>
        )}
        {!!showRecenter && !!onRecenter && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 12,
              width: "100%",
            }}
          >
            <Button color="primary" onClick={onRecenter} variant="contained">
              {translate("resources.locations-livetrack.labels.reCenter", {
                _: "Re-center",
              })}
            </Button>
          </div>
        )}
      </Paper>
    </ThemeProvider>
  </div>
);

const getMapControls = ({
  ...infoProps
}: InfoWindowProps): {
  controls: { [name: string]: ReactElement };
  controlPositions: { [name: string]: ControlPosition };
} => ({
  controls: {
    info: <InfoWindow {...infoProps} />,
  },
  controlPositions: {
    info: "TOP_CENTER",
  },
});

export const LocationLiveTrack = ({ id, tenantId }: Props) => {
  const translate = useTranslate();
  const theme = useTheme<Theme>();
  const version = useVersion();
  const [connectionToken, setConnectionToken] = useState<string>();
  const [error, setError] = useState<string>();
  const [autoFollow, setAutoFollow] = useState(true);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [resolvedVehicleId, setResolvedVehicleId] = useState<number>();
  const [vehicle, setVehicle] = useState<LiveTrackVehicleDto>();

  const mapRef = useRef<MapImperativeControls>(null);
  const hasAppliedInitialCameraRef = useRef(false);
  const ignoreNextZoomChangeRef = useRef(false);

  const isAuthenticated = isNumber(id);
  const authenticatedVehicleId = isAuthenticated ? Number(id) : undefined;
  const liveTrackingVehicleId = authenticatedVehicleId ?? resolvedVehicleId;

  const fallbackTrackEvent = useMemo(() => toSeedTrackEvent(vehicle), [vehicle]);
  const initialTrackEvent = fallbackTrackEvent;

  const {
    connectionTimedOut,
    predictionExpired,
    trackingLocation,
    trailPath,
    updateReceived,
  } =
    useLiveTrackingMotion({
      connectionToken,
      fallbackTrackEvent,
      initialTrackEvent,
      vehicleId: liveTrackingVehicleId,
    });

  const markerLocation = trackingLocation ?? fallbackTrackEvent;
  const resolvedMarkerLocation = hasValidCoordinates(markerLocation)
    ? markerLocation
    : undefined;
  const hasTrackingLocation = !!resolvedMarkerLocation;
  const markerBearing = trackingLocation?.bearing ?? fallbackTrackEvent?.bearing ?? 0;
  const { highlightedRoute, visitedRoute } = useMemo(
    () => getRenderedTrailRoutes(trailPath, trackingLocation),
    [trackingLocation, trailPath]
  );

  const liveStatusMessage = connectionTimedOut && !updateReceived
    ? translate("resources.locations-livetrack.labels.liveUpdatesUnavailable", {
      _: "Live updates unavailable. Showing the last known location.",
    })
    : !updateReceived
      ? translate("resources.locations-livetrack.labels.waitingForLiveUpdates", {
        _: "Waiting for live updates. Showing last known location.",
      })
      : predictionExpired
        ? translate("resources.locations-livetrack.labels.liveUpdatesPaused", {
          _: "Live updates paused. Showing the last known location.",
        })
        : undefined;

  const speedValue = updateReceived
    ? trackingLocation?.speed
    : vehicle?.currentLocation?.drivingSpeed;
  const speedUnit = updateReceived
    ? "mph"
    : vehicle?.currentLocation?.drivingSpeedUnit ??
    (vehicle?.odometer === Odometer.Miles ? "mph" : "kph");

  useEffect(() => {
    setAutoFollow(true);
    hasAppliedInitialCameraRef.current = false;
    ignoreNextZoomChangeRef.current = false;
  }, [liveTrackingVehicleId]);

  useEffect(() => {
    if (!mapLoaded || !resolvedMarkerLocation) {
      return;
    }

    if (!autoFollow) {
      return;
    }

    const center: google.maps.LatLngLiteral = {
      lat: resolvedMarkerLocation.lat,
      lng: resolvedMarkerLocation.lng,
    };
    if (!hasAppliedInitialCameraRef.current) {
      ignoreNextZoomChangeRef.current = true;
      mapRef.current?.setCenter(center);
      mapRef.current?.setZoom(LIVE_TRACKING_DEFAULT_ZOOM);
      hasAppliedInitialCameraRef.current = true;
      return;
    }

    mapRef.current?.setCenter(center);
  }, [
    autoFollow,
    mapLoaded,
    resolvedMarkerLocation,
    resolvedMarkerLocation?.lat,
    resolvedMarkerLocation?.lng,
  ]);

  const handleUserDragStart = () => {
    setAutoFollow(false);
  };

  const handleZoomChanged = () => {
    if (!hasAppliedInitialCameraRef.current) {
      return;
    }

    if (ignoreNextZoomChangeRef.current) {
      ignoreNextZoomChangeRef.current = false;
      return;
    }

    setAutoFollow(false);
  };

  const handleRecenter = () => {
    if (!resolvedMarkerLocation) {
      return;
    }

    mapRef.current?.setCenter({
      lat: resolvedMarkerLocation.lat,
      lng: resolvedMarkerLocation.lng,
    });
    setAutoFollow(true);
  };

  const map = loading ? (
    <Loading />
  ) : (
    <GoogleMap
      ref={mapRef}
      defaultMapType="hybrid"
      defaultZoomLevel={LIVE_TRACKING_DEFAULT_ZOOM}
      disableChildAutoPan
      error={error}
      gradientVisitedRoute
      highlightedRoute={highlightedRoute}
      highlightedRouteOpacity={0.28}
      criticalError={!hasTrackingLocation}
      onMapLoaded={setMapLoaded}
      onDragStart={handleUserDragStart}
      onZoomChanged={handleZoomChanged}
      routeColor="#321DE7"
      visitedRoute={visitedRoute}
      {...getMapControls({
        driverFullName:
          trackingLocation?.driverName ??
          vehicle?.currentLocation?.driverFullName ??
          "Unknown",
        drivingSpeed: formatSpeedValue(speedValue),
        drivingSpeedUnit: speedUnit,
        location:
          trackingLocation?.geolocation ??
          vehicle?.currentLocation?.location ??
          "Unknown",
        onRecenter: handleRecenter,
        statusMessage: liveStatusMessage,
        showRecenter: !autoFollow && hasTrackingLocation,
        theme,
        trailers:
          trackingLocation?.trailers ??
          vehicle?.currentLocation?.attachedTrailer ??
          "",
        translate,
      })}
    >
      {resolvedMarkerLocation && (
        <GoogleMapLiveTrackingMarker
          lat={resolvedMarkerLocation.lat}
          lng={resolvedMarkerLocation.lng}
          bearing={markerBearing}
          moving={
            updateReceived &&
            !predictionExpired &&
            (markerLocation?.speed ?? 0) > 0
          }
          vehicleName={vehicle?.name ?? ""}
        />
      )}
    </GoogleMap>
  );

  return (
    <Fragment key={version}>
      <Title
        title={
          <LocationLiveTrackTitle
            vehicle={vehicle}
            isAnonymous={!isAuthenticated}
          />
        }
      />
      {isAuthenticated && authenticatedVehicleId ? (
        <AuthenticatedLocationLiveTrack
          vehicleId={authenticatedVehicleId}
          setConnectionToken={setConnectionToken}
          setError={setError}
          setLoading={setLoading}
          setVehicle={setVehicle}
        >
          {map}
        </AuthenticatedLocationLiveTrack>
      ) : (
        <AnonymousLocationLiveTrack
          vehicleKey={id}
          tenantId={tenantId}
          setConnectionToken={setConnectionToken}
          setResolvedVehicleId={setResolvedVehicleId}
          setError={setError}
          setLoading={setLoading}
          setVehicle={setVehicle}
        >
          {map}
        </AnonymousLocationLiveTrack>
      )}
    </Fragment>
  );
};
